import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { AnimatePresence } from "framer-motion";
import { RestaurantHeader } from "@/components/RestaurantHeader";
import { CategoryFilter } from "@/components/CategoryFilter";
import { MenuItemCard } from "@/components/MenuItemCard";
import { CartDrawer } from "@/components/CartDrawer";
import { MenuItemDialog } from "@/components/MenuItemDialog";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useLanguage } from "@/contexts/LanguageContext";
import { RestaurantProvider } from "@/contexts/RestaurantContext";
import { RealtimeAIWaiter, RealtimeAIWaiterRef } from "@/components/RealtimeAIWaiter";
import { WelcomeOverlay } from "@/components/WelcomeOverlay";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { ReceiptDialog } from "@/components/ReceiptDialog";
import { CallWaiterButton } from "@/components/CallWaiterButton";
import { ChefAnswerNotifications } from "@/components/ChefAnswerNotifications";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, Bot } from "lucide-react";
import type { Restaurant, MenuItem } from "@/shared/schema";
import type { Language } from "@/lib/translations";
import { loadStripe } from '@stripe/stripe-js';
import { useToast } from "@/hooks/use-toast";
import { RatingDialog } from "@/components/RatingDialog";
import { calculateSalesTax } from "@/shared/salesTax";
import { resolveImageUrl } from "@/lib/imageUrl";

const restaurantImg = '/assets/stock_images/restaurant_interior__df0b7770.jpg';

interface CartItem {
  id: string;
  name: string;
  description: string;
  price: string;
  quantity: number;
  image: string;
  category: string;
  spiceLevel?: string | null;
  isVegan?: boolean;
  isVegetarian?: boolean;
  isHalal?: boolean;
  isKosher?: boolean;
  allergens?: string[] | null;
  customerNote?: string;
  allergies?: string;
}

function CustomerMenuContent() {
  const { id: restaurantId } = useParams<{ id: string }>();
  const { t, setLanguage, language } = useLanguage();
  const { toast } = useToast();
  const [activeCategory, setActiveCategory] = useState<string>("ALL");
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [paymentMethod, setPaymentMethod] = useState<'online' | 'cash'>('cash');
  const [aiTipAmount, setAiTipAmount] = useState<number | undefined>(undefined);
  const [aiCustomerNote, setAiCustomerNote] = useState<string | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [showWelcomeOverlay, setShowWelcomeOverlay] = useState(false);
  const [receiptOrderId, setReceiptOrderId] = useState<string | null>(null);
  const [receiptDialogOpen, setReceiptDialogOpen] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [ratingOrderData, setRatingOrderData] = useState<{ orderId: string; items: any[]; restaurantId: string } | null>(null);
  const floatingAIWaiterRef = useRef<RealtimeAIWaiterRef | null>(null);
  const paymentReturnProcessed = useRef(false);

  // Lead capture form state (for demo visitors from landing page)
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const leadFormShown = useRef(false);
  const [showAiWaiter, setShowAiWaiter] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  // Delay AI Waiter appearance by 13 seconds AFTER welcome overlay is dismissed
  // (Greeting plays immediately when dismissed, Lelah appears later)
  useEffect(() => {
    if (!welcomeDismissed) return;

    // 1. Pre-connect Lelah IMMEDIATELY in the background so she's ready when she appears
    console.log('[CustomerMenu] Welcome dismissed, pre-connecting Lelah in background...');
    if (floatingAIWaiterRef.current) {
      floatingAIWaiterRef.current.preConnect().catch((err: any) => {
        console.error('[CustomerMenu] Pre-connect failed:', err);
      });
    }

    // 2. Start 13-second timer for visual appearance
    console.log('[CustomerMenu] Welcome dismissed, starting 13-second timer for Lelah...');
    const timer = setTimeout(() => {
      console.log('[CustomerMenu] 13 seconds passed, showing Lelah...');
      setShowAiWaiter(true);
    }, 13000);

    return () => clearTimeout(timer);
  }, [welcomeDismissed]);

  // Generate or retrieve a stable customer session ID
  const customerSessionId = useMemo(() => {
    const key = `customer-session-${restaurantId}`;
    let sid = sessionStorage.getItem(key);
    if (!sid) {
      sid = `customer-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem(key, sid);
    }
    return sid;
  }, [restaurantId]);

  // Extract table Number from query params (e.g. ?table=1)
  const urlParams = new URLSearchParams(window.location.search);
  const tableNumber = urlParams.get('table');
  const showLeadForm = urlParams.get('showLeadForm') === 'true';

  // Show lead form dialog after a short delay if showLeadForm is true
  useEffect(() => {
    if (showLeadForm && !leadFormShown.current) {
      leadFormShown.current = true;
      // Show the dialog after the menu has loaded (2 second delay)
      const timer = setTimeout(() => {
        setShowLeadDialog(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showLeadForm]);

  const submitToHubSpot = async (data: { name: string; email: string; phone: string }, source: string) => {
    const portalId = "244843893";
    const formId = "b808aa91-1754-4c46-b1dc-c68e7f0912bb";
    const url = `https://api.hsforms.com/submissions/v3/integration/submit/${portalId}/${formId}`;

    const fields = [
      { name: "firstname", value: data.name },
      { name: "email", value: data.email },
      { name: "phone", value: data.phone }
    ];

    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields,
          context: {
            pageUri: window.location.href,
            pageName: document.title,
            hutk: document.cookie.match(/hubspotutk=([^;]+)/)?.[1]
          }
        })
      });
      console.log(`[HubSpot] Menu lead submitted from ${source}`);
    } catch (err) {
      console.error("[HubSpot] Submission error:", err);
    }
  };

  const handleLeadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Store lead data
    localStorage.setItem("waiterixDemoLead", JSON.stringify({ ...leadForm, ts: Date.now(), source: 'menu_demo' }));

    // Send to HubSpot
    submitToHubSpot(leadForm, 'menu_demo');

    setShowLeadDialog(false);
    setLeadForm({ name: "", email: "", phone: "" });
    toast({
      title: "Thanks for your interest!",
      description: "We'll be in touch soon to help you get started with Waiterix.",
    });
  };

  // Fetch tables to resolve table number to UUID
  const { data: tables = [] } = useQuery<any[]>({
    queryKey: ['/api/public/restaurant', restaurantId, 'tables'],
    queryFn: async () => {
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
      const res = await fetch(`${apiBaseUrl}/api/public/restaurant/${restaurantId}/tables`);
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!restaurantId,
  });

  const activeTable = tables.find(t => t.tableNumber === tableNumber);
  // Use the UUID if found, otherwise undefined (or fallback to number string for legacy compatibility if needed)
  const tableId = activeTable?.id;

  // Check if this is the first visit for this restaurant session
  useEffect(() => {
    if (restaurantId) {
      const hasSeenWelcome = sessionStorage.getItem(`restaurant-${restaurantId}-welcome-seen`);
      if (!hasSeenWelcome) {
        // First-time visitor: show welcome overlay, timer starts when dismissed
        setShowWelcomeOverlay(true);
      } else {
        // Returning visitor: start the 20-second timer immediately
        setWelcomeDismissed(true);
      }
    }
  }, [restaurantId]);

  // Fetch restaurant data
  const { data: restaurant, isLoading: restaurantLoading, error: restaurantError } = useQuery<Restaurant>({
    queryKey: ['/api/public/restaurant', restaurantId],
    queryFn: async () => {
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
      const res = await fetch(`${apiBaseUrl}/api/public/restaurant/${restaurantId}`);
      if (!res.ok) throw new Error('Restaurant not found');
      return res.json();
    },
    enabled: !!restaurantId,
  });

  // Set language to restaurant's default when restaurant data loads
  useEffect(() => {
    if (restaurant?.defaultLanguage && restaurantId) {
      const storedRestaurantLanguage = localStorage.getItem(`restaurant-${restaurantId}-language`);

      if (storedRestaurantLanguage) {
        // User has a preference for this restaurant, use it
        if (storedRestaurantLanguage !== language) {
          setLanguage(storedRestaurantLanguage as Language);
        }
      } else {
        // No preference stored, use restaurant's default
        setLanguage(restaurant.defaultLanguage as Language);
        localStorage.setItem(`restaurant-${restaurantId}-language`, restaurant.defaultLanguage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant?.defaultLanguage, restaurantId]);

  // Track language changes and store per-restaurant (when user manually changes language)
  useEffect(() => {
    if (restaurantId && language && restaurant) {
      const storedRestaurantLanguage = localStorage.getItem(`restaurant-${restaurantId}-language`);
      // Only update if language changed from what's stored
      if (storedRestaurantLanguage && storedRestaurantLanguage !== language) {
        localStorage.setItem(`restaurant-${restaurantId}-language`, language);
      }
    }
  }, [language, restaurantId, restaurant]);

  // Track table scan when menu loads
  useEffect(() => {
    const trackTableScan = async () => {
      // Only track if we have a valid restaurant and table UUID
      if (restaurantId && tableId) {
        // Use tableNumber for storage key to keep it simple/consistent with URL
        const hasTrackedScan = sessionStorage.getItem(`restaurant-${restaurantId}-table-${tableNumber}-tracked`);
        if (!hasTrackedScan) {
          try {
            const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
            await fetch(`${apiBaseUrl}/api/analytics/table-scan`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ restaurantId, tableId }), // Sending UUID
            });
            sessionStorage.setItem(`restaurant-${restaurantId}-table-${tableNumber}-tracked`, 'true');
          } catch (error) {
            console.error('Failed to track table scan:', error);
          }
        }
      }
    };

    trackTableScan();
  }, [restaurantId, tableId, tableNumber]);

  // Handle payment redirect return (CashApp Pay, Afterpay, etc.)
  useEffect(() => {
    const handlePaymentReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentIntent = urlParams.get('payment_intent');
      const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');

      // Check if this is a return from a redirect-based payment
      if (paymentIntent && paymentIntentClientSecret && !paymentReturnProcessed.current) {
        paymentReturnProcessed.current = true;
        console.log('[Payment Return] Detected payment redirect return');

        // Clean up payment URL parameters but preserve table number
        const tableParam = urlParams.get('table');
        const cleanUrl = tableParam ? `${window.location.pathname}?table=${tableParam}` : window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        // Get pending order data from localStorage
        const pendingOrderDataStr = localStorage.getItem('pendingOrder');
        if (!pendingOrderDataStr) {
          console.error('[Payment Return] No pending order data found in localStorage');
          toast({
            title: "Order Error",
            description: "Could not find pending order data. Please contact support if payment was charged.",
            variant: "destructive",
          });
          // Clean up URL but preserve table number
          const tableParam = urlParams.get('table');
          const cleanUrl = tableParam ? `${window.location.pathname}?table=${tableParam}` : window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          return;
        }

        const pendingOrderData = JSON.parse(pendingOrderDataStr);

        // Check if pending order data has expired (1 hour)
        const PENDING_ORDER_EXPIRY = 60 * 60 * 1000; // 1 hour in milliseconds
        const orderAge = Date.now() - pendingOrderData.timestamp;
        if (orderAge > PENDING_ORDER_EXPIRY) {
          console.error('[Payment Return] Pending order data has expired:', orderAge, 'ms old');
          localStorage.removeItem('pendingOrder');
          toast({
            title: "Order Expired",
            description: "This order session has expired. Please place a new order.",
            variant: "destructive",
          });
          // Clean up URL but preserve table number
          const tableParam = urlParams.get('table');
          const cleanUrl = tableParam ? `${window.location.pathname}?table=${tableParam}` : window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          return;
        }

        // Verify payment was successful using Stripe
        try {
          const stripeKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
          if (!stripeKey) {
            throw new Error('Stripe public key not configured');
          }

          const stripe = await loadStripe(stripeKey, {
            stripeAccount: pendingOrderData.stripeAccountId,
          });

          if (!stripe) {
            throw new Error('Failed to load Stripe');
          }

          // Retrieve the PaymentIntent to verify status
          const { paymentIntent: retrievedPaymentIntent } = await stripe.retrievePaymentIntent(paymentIntentClientSecret);

          if (retrievedPaymentIntent?.status === 'succeeded') {
            console.log('[Payment Return] Payment successful, creating order');

            // Create the order
            const response = await apiRequest("POST", "/api/orders", {
              ...pendingOrderData,
              stripePaymentIntentId: paymentIntent,
            });

            // Validate response before proceeding
            if (!response.ok) {
              const errorData = await response.json();
              console.error('[Payment Return] Order creation failed:', errorData);

              // Keep pending data for potential retry, show error
              toast({
                title: "Order Creation Failed",
                description: errorData.message || "Payment succeeded but order creation failed. Please contact support with your payment confirmation.",
                variant: "destructive",
              });

              return;
            }

            const data = await response.json();
            console.log('[Payment Return] Order created successfully:', data);

            // Clear pending order data only after successful creation
            localStorage.removeItem('pendingOrder');

            // Clear cart
            setCartItems([]);

            // For redirect-based payments (CashApp, Afterpay, etc.), DON'T show receipt dialog
            // because the payment provider already showed their confirmation/receipt page.
            // Just show a toast with an action to view receipt if they want.
            toast({
              title: "Payment Successful",
              description: "Your order has been placed successfully! Tap to view your receipt.",
              duration: 8000,
              action: (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setReceiptOrderId(data.id);
                    setReceiptDialogOpen(true);
                  }}
                >
                  View Receipt
                </Button>
              ),
            });
          } else {
            console.error('[Payment Return] Payment not successful:', retrievedPaymentIntent?.status);

            // Clear stale pending data since payment didn't succeed
            localStorage.removeItem('pendingOrder');

            toast({
              title: "Payment Issue",
              description: `Payment status: ${retrievedPaymentIntent?.status}. Please contact support if you were charged.`,
              variant: "destructive",
            });
            // Clean up URL but preserve table number
            const tableParam = urlParams.get('table');
            const cleanUrl = tableParam ? `${window.location.pathname}?table=${tableParam}` : window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        } catch (error) {
          console.error('[Payment Return] Error creating order after redirect:', error);

          // Keep pending data for potential retry
          toast({
            title: "Order Failed",
            description: "Payment verification or order creation failed. Please contact support with your payment confirmation.",
            variant: "destructive",
          });

          // Clean up URL but keep localStorage for support/retry and preserve table number
          const tableParam = urlParams.get('table');
          const cleanUrl = tableParam ? `${window.location.pathname}?table=${tableParam}` : window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    };

    handlePaymentReturn();
  }, [restaurantId, toast]);

  // Fetch menu items
  const { data: menuItems = [], isLoading: menuLoading } = useQuery<MenuItem[]>({
    queryKey: ['/api/menu-items', restaurantId],
    queryFn: async () => {
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
      const res = await fetch(`${apiBaseUrl}/api/menu-items?restaurantId=${restaurantId}`);
      if (!res.ok) throw new Error('Failed to fetch menu items');
      return res.json();
    },
    enabled: !!restaurantId,
  });

  // Fetch AI knowledge base for Lelah (includes extended menu details, restaurant knowledge, FAQs)
  const { data: aiKnowledge } = useQuery<{
    restaurant: {
      name: string;
      description: string;
      hours: string;
      address: string;
      city: string;
      phone: string;
      state: string;
    };
    menuItems: any[];
    restaurantKnowledge: {
      story?: string;
      philosophy?: string;
      sourcingPractices?: string;
      specialTechniques?: string;
      awards?: string;
      sustainabilityPractices?: string;
    } | null;
    faqKnowledge: Array<{
      question: string;
      answer: string;
    }>;
  }>({
    queryKey: ['/api/public/ai/knowledge', restaurantId],
    queryFn: async () => {
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
      const res = await fetch(`${apiBaseUrl}/api/public/ai/knowledge/${restaurantId}`);
      if (!res.ok) throw new Error('Failed to fetch AI knowledge base');
      return res.json();
    },
    enabled: !!restaurantId && !!restaurant?.aiWaiterEnabled,
  });

  // Get unique categories from menu items
  const actualCategories = Array.from(new Set(menuItems.map(item => item.category)));
  const categories = [
    { value: "ALL", label: t('allCategories') },
    ...actualCategories.map(cat => ({ value: cat, label: cat }))
  ];

  const filteredItems = activeCategory === "ALL"
    ? menuItems.filter(item => item.available)
    : menuItems.filter(item => item.category === activeCategory && item.available);

  const addToCart = (item: MenuItem, quantity: number = 1) => {
    // Determine actual quantity to add (AI might pass quantity in item object)
    const quantityToAdd = (item as any).quantity || quantity;

    console.log('[CustomerMenu] addToCart called with item:', item.name, 'qty:', quantityToAdd);

    const cartItem: CartItem = {
      id: item.id,
      name: item.name,
      description: item.description,
      price: item.price,
      quantity: quantityToAdd,
      image: resolveImageUrl(item.imageUrl, restaurantImg),
      category: item.category,
      spiceLevel: item.spiceLevel,
      isVegan: item.isVegan,
      isVegetarian: item.isVegetarian,
      isHalal: item.isHalal,
      isKosher: item.isKosher,
      allergens: item.allergens,
      customerNote: (item as any).customerNote,
      allergies: (item as any).allergies,
    };

    setCartItems(prevItems => {
      const existingItem = prevItems.find(ci => ci.id === item.id);
      if (existingItem) {
        console.log('[CustomerMenu] Item exists in cart, updating quantity');
        return prevItems.map(ci =>
          ci.id === item.id ? { ...ci, quantity: ci.quantity + quantityToAdd, customerNote: cartItem.customerNote || ci.customerNote, allergies: cartItem.allergies || ci.allergies } : ci
        );
      } else {
        console.log('[CustomerMenu] Adding new item to cart');
        return [...prevItems, cartItem];
      }
    });

    toast({
      title: "Added to Cart",
      description: `${quantityToAdd}x ${item.name}`,
    });
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity === 0) {
      setCartItems(cartItems.filter(item => item.id !== id));
    } else {
      setCartItems(cartItems.map(item =>
        item.id === id ? { ...item, quantity } : item
      ));
    }
  };

  const removeFromCart = (item: { id: string; name: string; quantity?: number }) => {
    console.log('[CustomerMenu] removeFromCart called with item:', item.name, 'qty:', item.quantity);

    setCartItems(prevItems => {
      const existingItem = prevItems.find(ci => ci.id === item.id);
      if (!existingItem) return prevItems;

      const quantityToRemove = item.quantity || 1;

      if (existingItem.quantity > quantityToRemove) {
        return prevItems.map(ci =>
          ci.id === item.id ? { ...ci, quantity: ci.quantity - quantityToRemove } : ci
        );
      } else {
        return prevItems.filter(ci => ci.id !== item.id);
      }
    });

    toast({
      title: "Removed from Cart",
      description: item.quantity ? `${item.quantity}x ${item.name}` : item.name,
    });
  };

  const handleCheckout = () => {
    setCheckoutOpen(true);
  };

  const handleCheckoutSuccess = () => {
    setCartItems([]);
    setCheckoutOpen(false);
  };

  const handleOrderComplete = (orderId: string) => {
    // Store order data for rating (will be shown after receipt)
    setRatingOrderData({
      orderId,
      items: cartItems.map(item => ({
        menuItemId: item.id,
        name: item.name,
        quantity: item.quantity,
      })),
      restaurantId: restaurantId || '',
    });

    // Store receipt order ID
    setReceiptOrderId(orderId);

    // Show receipt first
    setReceiptDialogOpen(true);

    // Close checkout dialog
    setCheckoutOpen(false);
  };

  const subtotal = cartItems.reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0);
  const tax = calculateSalesTax(subtotal, restaurant?.state);
  const total = subtotal + tax;

  // Loading state
  if (restaurantLoading || menuLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Restaurant header skeleton */}
          <div className="relative mb-8 overflow-hidden rounded-xl">
            <Skeleton className="h-64 w-full pulse-enhanced" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            <div className="absolute bottom-6 left-6 right-6">
              <Skeleton className="h-8 w-3/4 mb-2 pulse-enhanced" />
              <Skeleton className="h-4 w-1/2 pulse-enhanced" />
            </div>
          </div>

          {/* Menu navigation skeleton */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <Skeleton className="h-6 w-20 pulse-enhanced" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24 pulse-enhanced" />
                <Skeleton className="h-10 w-24 pulse-enhanced" />
                <Skeleton className="h-10 w-24 pulse-enhanced" />
              </div>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-8 w-20 pulse-enhanced" />
              ))}
            </div>
          </div>

          {/* Menu items skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="glass-card rounded-lg overflow-hidden">
                <Skeleton className="h-48 w-full pulse-enhanced" />
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <Skeleton className="h-6 w-3/4 pulse-enhanced" />
                    <Skeleton className="h-6 w-16 pulse-enhanced" />
                  </div>
                  <Skeleton className="h-4 w-full mb-2 pulse-enhanced" />
                  <Skeleton className="h-4 w-2/3 mb-4 pulse-enhanced" />
                  <div className="flex gap-2 mb-4">
                    <Skeleton className="h-6 w-16 pulse-enhanced" />
                    <Skeleton className="h-6 w-20 pulse-enhanced" />
                  </div>
                  <Skeleton className="h-10 w-full pulse-enhanced" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (restaurantError || !restaurant) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Restaurant Not Found</AlertTitle>
          <AlertDescription>
            The restaurant you're looking for doesn't exist or has been removed.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RestaurantHeader
        name={restaurant.name}
        description={restaurant.description || ""}
        image={
          restaurant.coverImageUrl
            ? (restaurant.coverImageUrl.startsWith('http')
              ? restaurant.coverImageUrl
              : (restaurant.coverImageUrl.startsWith('/api/') || restaurant.coverImageUrl.startsWith('/objects/')
                ? `${((import.meta as any).env.VITE_API_BASE_URL || '')}${restaurant.coverImageUrl}`
                : (restaurant.coverImageUrl.startsWith('/')
                  ? restaurant.coverImageUrl
                  : `/assets/stock_images/${restaurant.coverImageUrl}`)))
            : restaurantImg
        }
        address={restaurant.address || ""}
        phone={restaurant.phone || ""}
        hours={restaurant.hours || ""}
      />

      <div className="sticky top-0 z-40 glass backdrop-blur-md border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h2 className="text-xl font-semibold">{t('menu')}</h2>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <CallWaiterButton
                restaurantId={restaurantId || ''}
                tableId={tableId || undefined}
                variant="outline"
                className="hover-lift"
              />
              <CartDrawer
                items={cartItems.map(item => ({
                  ...item,
                  price: parseFloat(item.price),
                }))}
                onUpdateQuantity={updateQuantity}
                onRemoveItem={(id) => setCartItems(cartItems.filter(item => item.id !== id))}
                paymentMethod={paymentMethod}
                onPaymentMethodChange={setPaymentMethod}
                onCheckout={handleCheckout}
                stripeOnboardingComplete={restaurant.stripeOnboardingComplete}
                paystackOnboardingComplete={!!restaurant.paystackSubaccountCode}
              />
            </div>
          </div>
          <CategoryFilter
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
          />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-chart-2/5 pointer-events-none"></div>
        {filteredItems.length === 0 ? (
          <div className="text-center py-20 relative z-10">
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-muted to-muted/50 rounded-full flex items-center justify-center">
                <Bot className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No Items Available</h3>
              <p className="text-muted-foreground">No menu items available in this category at the moment.</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative z-10">
            {filteredItems.map((item, index) => (
              <div
                key={item.id}
                className="bounce-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <MenuItemCard
                  name={item.name}
                  description={item.description}
                  price={parseFloat(item.price as any)}
                  image={
                    item.imageUrl
                      ? (item.imageUrl.startsWith('http')
                        ? item.imageUrl
                        : (item.imageUrl.startsWith('/api/') || item.imageUrl.startsWith('/objects/')
                          ? `${((import.meta as any).env.VITE_API_BASE_URL || '')}${item.imageUrl}`
                          : (item.imageUrl.startsWith('/')
                            ? item.imageUrl
                            : `/assets/stock_images/${item.imageUrl}`)))
                      : restaurantImg
                  }
                  spiceLevel={item.spiceLevel ? parseInt(item.spiceLevel) : undefined}
                  isVegan={item.isVegan}
                  isVegetarian={item.isVegetarian}
                  isHalal={item.isHalal}
                  isKosher={item.isKosher}
                  allergens={item.allergens || []}
                  onAddToCart={() => addToCart(item)}
                  onClick={() => setSelectedItem(item)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedItem && (
        <MenuItemDialog
          open={!!selectedItem}
          onOpenChange={(open) => !open && setSelectedItem(null)}
          item={{
            ...selectedItem,
            price: parseFloat(selectedItem.price as any),
            spiceLevel: selectedItem.spiceLevel ? parseInt(selectedItem.spiceLevel) : undefined,
            allergens: selectedItem.allergens || [],
            image: selectedItem.imageUrl
              ? (selectedItem.imageUrl.startsWith('http')
                ? selectedItem.imageUrl
                : (selectedItem.imageUrl.startsWith('/api/') || selectedItem.imageUrl.startsWith('/objects/')
                  ? `${((import.meta as any).env.VITE_API_BASE_URL || '')}${selectedItem.imageUrl}`
                  : (selectedItem.imageUrl.startsWith('/')
                    ? selectedItem.imageUrl
                    : `/assets/stock_images/${selectedItem.imageUrl}`)))
              : restaurantImg,
          }}
          onAddToCart={(quantity) => addToCart(selectedItem, quantity)}
        />
      )}

      {restaurantId && (
        <CheckoutDialog
          open={checkoutOpen}
          onOpenChange={setCheckoutOpen}
          items={cartItems.map(item => ({
            ...item,
            price: parseFloat(item.price),
          }))}
          subtotal={subtotal}
          tax={tax}
          total={total}
          paymentMethod={paymentMethod}
          restaurantId={restaurantId}
          tableId={tableId || undefined}
          onSuccess={handleCheckoutSuccess}
          onOrderComplete={handleOrderComplete}
          initialTipAmount={aiTipAmount}
          initialCustomerNote={aiCustomerNote}
        />
      )}

      {/* Receipt Dialog */}
      <ReceiptDialog
        orderId={receiptOrderId}
        open={receiptDialogOpen}
        onOpenChange={(open) => {
          setReceiptDialogOpen(open);
          // Clear cart and show rating when receipt dialog is closed
          if (!open) {
            setCartItems([]);
            if (ratingOrderData) {
              setRatingDialogOpen(true);
            }
          }
        }}
      />

      {/* Rating Dialog - shown after receipt is closed */}
      {ratingOrderData && (
        <RatingDialog
          open={ratingDialogOpen}
          orderId={ratingOrderData.orderId}
          restaurantId={ratingOrderData.restaurantId}
          items={ratingOrderData.items}
          onClose={() => {
            setRatingDialogOpen(false);
            setRatingOrderData(null);
          }}
        />
      )}

      {/* Welcome overlay - shown on first visit only if AI is enabled */}
      <AnimatePresence>
        {showWelcomeOverlay && restaurant && restaurant.aiWaiterEnabled && (
          <WelcomeOverlay
            restaurantName={restaurant.name}
            onDismiss={async () => {
              // IMMEDIATELY hide overlay and mark dismissed (before any async operations)
              setShowWelcomeOverlay(false);
              setWelcomeDismissed(true);

              // Mark that user has seen the welcome
              if (restaurantId) {
                sessionStorage.setItem(`restaurant-${restaurantId}-welcome-seen`, 'true');
              }

              // Request microphone permission for wake word detection (user gesture context)
              // This runs in background - doesn't block UI
              const wakeWordEnabled = localStorage.getItem('waiterix-wake-word-enabled');
              if (wakeWordEnabled === null || wakeWordEnabled === 'true') {
                try {
                  console.log('[CustomerMenu] Requesting mic permission for wake word detection...');
                  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                  console.log('[CustomerMenu] Mic permission granted');
                  // Store permission grant
                  sessionStorage.setItem('waiterix-mic-permission-granted', 'true');
                  // Stop the stream immediately - we just needed permission
                  stream.getTracks().forEach(track => track.stop());
                } catch (err) {
                  console.warn('[CustomerMenu] Mic permission denied:', err);
                  sessionStorage.setItem('waiterix-mic-permission-granted', 'false');
                }
              }

              // Play greeting audio in background (doesn't block UI)
              try {
                const backendBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
                if (backendBaseUrl) {
                  const greetingUrl = `${backendBaseUrl}/api/public/ai/greeting/${restaurantId}?lang=${language}`;
                  console.log('[CustomerMenu] Playing instant greeting from:', greetingUrl);

                  const response = await fetch(greetingUrl);
                  if (response.ok) {
                    const arrayBuffer = await response.arrayBuffer();

                    // Check if data is base64 encoded
                    const firstBytes = new Uint8Array(arrayBuffer.slice(0, 20));
                    const firstChars = String.fromCharCode(...firstBytes);

                    let audioData: ArrayBuffer;
                    if (firstChars.match(/^[A-Za-z0-9+/=]+$/)) {
                      // Decode base64
                      const base64String = new TextDecoder().decode(arrayBuffer);
                      const binaryString = atob(base64String);
                      const bytes = new Uint8Array(binaryString.length);
                      for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                      }
                      audioData = bytes.buffer;
                    } else {
                      audioData = arrayBuffer;
                    }

                    const blob = new Blob([audioData], { type: 'audio/mpeg' });
                    const audioUrl = URL.createObjectURL(blob);
                    const welcomeAudio = new Audio(audioUrl);

                    welcomeAudio.onended = () => {
                      URL.revokeObjectURL(audioUrl);
                      console.log('[CustomerMenu] Greeting finished');
                    };

                    welcomeAudio.play().catch(err => {
                      console.warn('[CustomerMenu] Audio play prevented:', err);
                    });
                  }
                }
              } catch (err) {
                console.error('[CustomerMenu] Failed to play greeting:', err);
              }
            }}
          />
        )}
      </AnimatePresence>

      {/* Chef Answer Notifications - polls for responses even when Lelah is off */}
      {restaurantId && (
        <ChefAnswerNotifications
          restaurantId={restaurantId}
          customerSessionId={customerSessionId}
        />
      )}

      {/* AI Waiter - Realtime version using OpenAI Realtime API via Render WebSocket */}
      {restaurantId && restaurant?.aiWaiterEnabled && (
        <RealtimeAIWaiter
          ref={floatingAIWaiterRef as React.Ref<RealtimeAIWaiterRef>}
          restaurantId={restaurantId}
          customerSessionId={customerSessionId}
          restaurantName={restaurant.name}
          restaurantState={restaurant.state ?? undefined}
          restaurantDescription={aiKnowledge?.restaurant?.description ?? restaurant.description ?? undefined}
          restaurantHours={aiKnowledge?.restaurant?.hours ?? restaurant.hours ?? undefined}
          restaurantPhone={aiKnowledge?.restaurant?.phone ?? restaurant.phone ?? undefined}
          restaurantAddress={aiKnowledge?.restaurant?.address ? `${aiKnowledge.restaurant.address}${aiKnowledge.restaurant.city ? ', ' + aiKnowledge.restaurant.city : ''}` : (restaurant.address ?? undefined)}
          tableId={tableId}
          tableNumber={tableNumber || undefined}
          menuItems={aiKnowledge?.menuItems || menuItems}
          restaurantKnowledge={aiKnowledge?.restaurantKnowledge}
          faqKnowledge={aiKnowledge?.faqKnowledge}
          currentCart={cartItems.map(item => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            customerNote: item.customerNote,
            allergies: item.allergies,
          }))}
          onAddToCart={(item) => addToCart(item)}
          onRemoveFromCart={(item) => removeFromCart(item)}
          onOrderConfirmed={(order) => {
            // Order was placed via AI Waiter voice - show receipt
            console.log('[CustomerMenu] Order confirmed via AI:', order);

            // API returns 'orderId' not 'id'
            const orderId = order.orderId || order.id;

            if (orderId) {
              // Use items from backend order if available (more reliable), fallback to frontend cart
              const itemsForRating = (order.items && order.items.length > 0)
                ? order.items.map((item: any) => ({
                  menuItemId: item.id,
                  name: item.name,
                  quantity: item.quantity,
                }))
                : cartItems.map(item => ({
                  menuItemId: item.id,
                  name: item.name,
                  quantity: item.quantity,
                }));

              // Store order data for rating dialog
              setRatingOrderData({
                orderId,
                items: itemsForRating,
                restaurantId: restaurantId || '',
              });

              // Clear cart since order was placed server-side
              setCartItems([]);

              // Show receipt dialog
              setReceiptOrderId(orderId);
              setReceiptDialogOpen(true);

              toast({
                title: "Order Placed! 🎉",
                description: `Your order #${orderId.slice(-8).toUpperCase()} has been sent to the kitchen.`,
              });
            } else {
              // No order ID - just show toast confirmation
              setCartItems([]);
              toast({
                title: "Order Placed! 🎉",
                description: order.message || "Your order has been sent to the kitchen.",
              });
            }
          }}
          onCheckout={(tipAmount, customerNote) => {
            console.log('[CustomerMenu] AI requested checkout', { tipAmount, customerNote });
            setAiTipAmount(tipAmount);
            setAiCustomerNote(customerNote);
            setPaymentMethod('online'); // Default to online/card since they said "pay here"
            handleCheckout();
          }}
          initiallyHidden={!showAiWaiter}
        />
      )}


      {/* Lead Capture Dialog - shown for demo visitors from landing page */}
      <Dialog open={showLeadDialog} onOpenChange={() => { }}>
        <DialogContent
          className="sm:max-w-md [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Welcome to Waiterix Demo!</DialogTitle>
            <DialogDescription>
              Please share your details to continue exploring our AI Waiter. We'll reach out to help you get started.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleLeadSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lead-name">Name</Label>
              <Input
                id="lead-name"
                placeholder="Your name"
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead-email">Email</Label>
              <Input
                id="lead-email"
                type="email"
                placeholder="you@example.com"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lead-phone">Phone Number</Label>
              <Input
                id="lead-phone"
                type="tel"
                placeholder="+1 (555) 000-0000"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                required
              />
            </div>

            <Button type="submit" className="w-full">
              Continue to Demo
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Waiterix Branding Footer */}
      <footer className="mt-8 pb-4 text-center border-t pt-4">
        <a
          href="https://waiterix.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-muted-foreground hover-elevate inline-block px-3 py-1.5 rounded-md transition-colors"
          data-testid="link-waiterix-branding"
        >
          Powered by Waiterix.com - AI Ordering Made Simple
        </a>
      </footer>
    </div>
  );
}

export default function CustomerMenu() {
  const { id: restaurantId } = useParams<{ id: string }>();

  // Fetch restaurant data for RestaurantProvider
  const { data: restaurant } = useQuery<Restaurant>({
    queryKey: ['/api/public/restaurant', restaurantId],
    queryFn: async () => {
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';
      const res = await fetch(`${apiBaseUrl}/api/public/restaurant/${restaurantId}`);
      if (!res.ok) throw new Error('Restaurant not found');
      return res.json();
    },
    enabled: !!restaurantId,
  });

  // Wrap with RestaurantProvider to sync currency and language
  return (
    <RestaurantProvider
      currencyCode={restaurant?.currencyCode}
      defaultLanguage={restaurant?.defaultLanguage}
    >
      <CustomerMenuContent />
    </RestaurantProvider>
  );
}
